import React from 'react';
import type { LandingCommunicationContent } from '../content/types';
import type { LandingBlockTone } from './LandingPitchBlock';

export type LandingInstructionBlockProps = {
  content: LandingCommunicationContent['instructions'];
  tone?: LandingBlockTone;
  className?: string;
};

const toneStyles: Record<
  LandingBlockTone,
  {
    heading: string;
    label: string;
    body: string;
    card: string;
    numberBg: string;
    numberText: string;
  }
> = {
  dark: {
    heading: 'text-white',
    label: 'text-white',
    body: 'text-white/75',
    card: 'border-white/10 bg-white/[0.03]',
    numberBg: 'bg-white/10',
    numberText: 'text-white',
  },
  light: {
    heading: 'text-slate-950',
    label: 'text-slate-950',
    body: 'text-slate-600',
    card: 'border-slate-200 bg-white',
    numberBg: 'bg-slate-100',
    numberText: 'text-slate-900',
  },
  accent: {
    heading: 'text-white',
    label: 'text-white',
    body: 'text-white/85',
    card: 'border-white/20 bg-white/10',
    numberBg: 'bg-white/20',
    numberText: 'text-white',
  },
};

/**
 * LandingInstructionBlock — ordered next-steps block.
 * Instructional, not a checkbox list.
 */
export default function LandingInstructionBlock({
  content,
  tone = 'dark',
  className = '',
}: LandingInstructionBlockProps) {
  if (!content || !content.steps || content.steps.length === 0) return null;
  const s = toneStyles[tone];
  return (
    <div
      data-edit-tag={content.editTag}
      className={`mx-auto max-w-3xl ${className}`.trim()}
    >
      {content.headline ? (
        <h3
          className={`text-2xl font-semibold tracking-tight md:text-3xl ${s.heading}`}
        >
          {content.headline}
        </h3>
      ) : null}
      <ol className={`${content.headline ? 'mt-6' : ''} space-y-3`}>
        {content.steps.map((step, i) => (
          <li
            key={i}
            data-edit-tag={step.editTag}
            className={`flex gap-4 rounded-2xl border p-5 ${s.card}`}
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${s.numberBg} ${s.numberText}`}
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className={`text-base font-semibold leading-7 ${s.label}`}>
                {step.label}
              </p>
              {step.body ? (
                <p className={`mt-1 text-sm leading-6 md:text-base md:leading-7 ${s.body}`}>
                  {step.body}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}